import createClient from "openapi-fetch";

import type { components, paths } from "./schema.gen";

export type Schemas = components["schemas"];
export type User = Schemas["User"];
export type Me = Schemas["Me"];
export type AuthResponse = Schemas["AuthResponse"];
export type Problem = Schemas["Problem"];

/**
 * Access token lives only in memory (docs/06-api.md). The refresh token is an
 * HttpOnly cookie the browser sends to /api/v1/auth/* by itself.
 */
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function setSessionLostHandler(fn: (() => void) | null) {
  onSessionLost = fn;
}

/** Error thrown by query functions; carries the RFC 9457 problem. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Validation errors: field → code. */
  readonly fields: Record<string, string>;

  constructor(status: number, problem?: Partial<Problem>) {
    super(problem?.detail || problem?.code || `HTTP ${status}`);
    this.status = status;
    this.code = problem?.code ?? "unknown";
    this.fields = Object.fromEntries((problem?.errors ?? []).map((e) => [e.field, e.code]));
  }
}

const origin = () => window.location.origin;

let refreshing: Promise<AuthResponse | null> | null = null;

/** Exchanges the refresh cookie for a new access token. Concurrent callers share one request. */
export function refreshSession(): Promise<AuthResponse | null> {
  refreshing ??= (async () => {
    try {
      const res = await globalThis.fetch(`${origin()}/api/v1/auth/refresh`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!res.ok) return null;
      const body = (await res.json()) as AuthResponse;
      accessToken = body.access_token;
      return body;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

const isAuthPath = (url: string) => new URL(url).pathname.startsWith("/api/v1/auth/");

/** fetch with the access token; on 401 refreshes once and retries. */
async function authFetch(request: Request): Promise<Response> {
  const retry = request.clone();
  const withToken = (r: Request) => {
    if (accessToken) r.headers.set("Authorization", `Bearer ${accessToken}`);
    return r;
  };
  const res = await globalThis.fetch(withToken(request));
  if (res.status !== 401 || isAuthPath(request.url)) return res;

  const session = await refreshSession();
  if (!session) {
    accessToken = null;
    onSessionLost?.();
    return res;
  }
  return globalThis.fetch(withToken(retry));
}

/** Typed API client generated from api/openapi.yaml. */
export const api = createClient<paths>({
  baseUrl: origin(),
  credentials: "same-origin",
  // Resolved on every call so tests can replace fetch.
  fetch: (request) => authFetch(request),
});

/** Unwraps an openapi-fetch result or throws ApiError. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || result.data === undefined) {
    throw new ApiError(result.response.status, result.error as Partial<Problem> | undefined);
  }
  return result.data;
}

/** Checks a result without a body (204) or throws ApiError. */
export function unwrapVoid(result: { error?: unknown; response: Response }): void {
  if (result.error !== undefined || !result.response.ok) {
    throw new ApiError(result.response.status, result.error as Partial<Problem> | undefined);
  }
}
